import DataStructure, { sanitize } from "./DataStructure.js";
import { deepFreeze, formatPercentage } from "../util/misc.js";
import { i18n } from "../boot/i18n.js";
import { compact, defaults, every, isEqual, isString } from "lodash";
import { addDays, parse } from "date-fns";

export const VENDOR_TERMS_FIELDS = {
  type: "",
  label: "",
  isDateDriven: false,
  netDays: null,
  netBuffer: null,
  discount: null,
  discountDays: null,
};
deepFreeze(VENDOR_TERMS_FIELDS);

export const VENDOR_TERMS = {
  Net30: {
    label: "Net 30",
    isDateDriven: false,
    netDays: 30,
    discount: null,
    discountDays: null,
  },
  "1%10Net30": {
    label: "1% 10 Net 30",
    isDateDriven: false,
    netDays: 30,
    discount: 1,
    discountDays: 10,
  },
};
deepFreeze(VENDOR_TERMS);

export default class VendorTerms extends DataStructure {
  constructor(data = {}) {
    if (data && data.type in VENDOR_TERMS) {
      // `data` may be a frozen store object; default into a fresh {} so we
      // never mutate the frozen source (lodash defaults mutates its 1st arg).
      data = defaults({}, data, VENDOR_TERMS[data.type]);
    }
    super(sanitize(data, VENDOR_TERMS_FIELDS));
  }

  setType(typeID) {
    const type = VENDOR_TERMS[typeID];
    if (type) {
      Object.assign(this.data, type);
    } else {
      Object.assign(this.data, {
        ...VENDOR_TERMS_FIELDS,
        type: typeID,
      });
    }
    return this;
  }

  get rules() {
    return {
      label: [
        (a) => Boolean(a) || "required",
        (a) =>
          !this.isCustom ||
          !Object.values(VENDOR_TERMS).find((t) => t.label === a) ||
          "labelConflict",
      ],
      netDays: [
        (a) => Boolean(a) || "required",
        (a) =>
          (this.data.isDateDriven ? a <= 31 : a <= 365) || "netDaysExceedsMax",
      ],
      netBuffer: [(a) => !a || (a <= 31 && a > 0) || "netBufferRange"],
      discount: [(a) => !a || (a < 100 && a > 0) || "discountRange"],
      discountDays: [
        (a) =>
          !this.data.discount ||
          (a > 0 && a < this.data.netDays) ||
          "discountDaysRange",
      ],
    };
  }

  get isValid() {
    return every(this.rules, (rules, key) =>
      rules.every((rule) => rule(this.data[key]) === true)
    );
  }

  get isCustom() {
    const type = this.type;
    return (
      this.data.type &&
      (!type ||
        !isEqual(this.data, {
          ...this.data,
          ...type,
        }))
    );
  }

  getDueDate(invoiceDate) {
    if (!this.data.netDays) {
      return invoiceDate;
    }
    if (isString(invoiceDate)) {
      invoiceDate = parse(
        invoiceDate,
        i18n.global.t("format.date-standard"),
        new Date()
      );
    }
    return addDays(invoiceDate, this.data.netDays);
  }

  getDiscountDate(invoiceDate) {
    if (!this.data.discount || !this.data.discountDays) {
      return null;
    }
    if (isString(invoiceDate)) {
      invoiceDate = parse(
        invoiceDate,
        i18n.global.t("format.date-standard"),
        new Date()
      );
    }
    return addDays(invoiceDate, this.data.discountDays);
  }

  // Formatting

  get type() {
    return VENDOR_TERMS[this.data.type] || null;
  }

  get label() {
    const key = "terms.vendor.types." + this.data.label;
    return i18n.global.te(key)
      ? i18n.global.t(key)
      : i18n.global.te(this.data.label)
      ? i18n.global.t(this.data.label)
      : this.data.label;
  }

  get discountPercent() {
    return formatPercentage(this.data.discount);
  }

  toArray() {
    const key =
      "terms.vendor." + (this.isDateDriven ? "dateDriven." : "standard.");
    return compact([
      this.data.netDays
        ? compact([
            i18n.global.tc(key + "netDays.prefix", this.data.netDays),
            this.netDays,
            i18n.global.tc(key + "netDays.suffix", this.data.netDays),
          ]).join(" ")
        : null,
      this.data.isDateDriven && this.data.netBuffer
        ? compact([
            i18n.global.tc(key + "netBuffer.prefix", this.data.netBuffer),
            this.netBuffer,
            i18n.global.tc(key + "netBuffer.suffix", this.data.netBuffer),
          ]).join(" ")
        : null,
      this.data.discount
        ? compact([
            i18n.global.tc(key + "discount.prefix", this.data.discount),
            this.discount + "%",
          ]).join(" ")
        : null,
      this.data.discountDays
        ? compact([
            i18n.global.tc(key + "discountDays.prefix", this.data.discountDays),
            this.discountDays,
            i18n.global.tc(key + "discountDays.suffix", this.data.discountDays),
          ]).join(" ")
        : null,
    ]);
  }

  toString(options = { html: false }) {
    return this.toArray().join(options.html ? "<br/>" : "\n");
  }
}
